import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

// Icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

// Custom Grid components
import GridContainer from '../common/GridContainer';
import GridItem from '../common/GridItem';

// Footer links data
import { footerLinks } from '../../data/footerLinks';

const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'primary.main',
        color: 'white',
        py: 4,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Container maxWidth="lg">
        <GridContainer spacing={3}>
          {/* Company Info */}
          <GridItem xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <DirectionsCarIcon
                sx={{
                  mr: 1,
                  fontSize: '2rem',
                  color: 'secondary.main',
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Playfair Display, serif',
                  fontWeight: 700,
                  letterSpacing: '.1rem',
                }}
              >
                THE UNITED TRAVEL
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 450 }}
            >
              Setting the standard for reliable transportation services for all needs. From standard sedans to luxury vehicles, our professional fleet and experienced team ensure your journey is comfortable, reliable, and always on time.
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  F-8 Plot No. 347/2, Third Floor, Surbhi Complex, Sector - 22, Gandhinagar - 382021
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  +91 6351003667
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  contact.theunitedtravels@gmail.com
                </Typography>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, color: 'white', fontWeight: 600 }}
                >
                  Follow Us
                </Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    aria-label="Facebook"
                    size="small"
                    href="https://www.facebook.com/theunitedtravels"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Twitter"
                    size="small"
                    href="https://x.com/theunitedtrave1"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    <TwitterIcon />
                  </IconButton>
                  <IconButton
                    aria-label="LinkedIn"
                    size="small"
                    href="https://www.linkedin.com/in/the-united-travel-068874192"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Instagram"
                    size="small"
                    href="https://www.instagram.com/unitedcabofficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          </GridItem>

          {/* Links - Equal width columns */}
          <GridItem xs={12} md={8}>
            <GridContainer spacing={2}>
              {/* Company */}
              <GridItem xs={12} sm={6} md={4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: 'white' }}
                >
                  {footerLinks[0].title}
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks[0].links.map((link) => (
                    <Link
                      key={link.name}
                      component={RouterLink}
                      to={link.path}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'secondary.main',
                        },
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              </GridItem>

              {/* Services */}
              <GridItem xs={12} sm={6} md={4}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: 'white' }}
                >
                  {footerLinks[1].title}
                </Typography>
                <Stack spacing={1.5}>
                  {footerLinks[1].links.map((link) => (
                    <Link
                      key={link.name}
                      component={RouterLink}
                      to={link.path}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'secondary.main',
                        },
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              </GridItem>

              {/* Fleet and Support in a single column */}
              <GridItem xs={12} sm={12} md={4}>
                <GridContainer spacing={2}>
                  {/* Fleet Column */}
                  <GridItem xs={6} md={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: 'white' }}
                    >
                      {footerLinks[2].title}
                    </Typography>
                    <Stack spacing={1.5}>
                      {footerLinks[2].links.map((link) => (
                        <Link
                          key={link.name}
                          component={RouterLink}
                          to={link.path}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            textDecoration: 'none',
                            '&:hover': {
                              color: 'secondary.main',
                            },
                            transition: 'color 0.3s ease',
                          }}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </Stack>
                  </GridItem>
                  
                  {/* Support Column */}
                  <GridItem xs={6} md={6}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2, color: 'white' }}
                    >
                      {footerLinks[3].title}
                    </Typography>
                    <Stack spacing={1.5}>
                      {footerLinks[3].links.map((link) => (
                        <Link
                          key={link.name}
                          component={RouterLink}
                          to={link.path}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            textDecoration: 'none',
                            '&:hover': {
                              color: 'secondary.main',
                            },
                            transition: 'color 0.3s ease',
                          }}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </Stack>
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>

        <Divider sx={{ my: 3, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'flex-start' },
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: { xs: 2, sm: 0 } }}>
            © {new Date().getFullYear()} The United Travel. All rights reserved.
          </Typography>
          <Box>
            <Link 
              component={RouterLink} 
              to="/terms"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                mr: 3,
                '&:hover': { color: 'secondary.main' },
              }}
            >
              Terms of Service
            </Link>
            <Link 
              component={RouterLink} 
              to="/privacy"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                '&:hover': { color: 'secondary.main' },
              }}
            >
              Privacy Policy
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 